import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Card, 
  CardContent, 
  Grid, 
  Chip, 
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Paper
} from '@mui/material';
import { 
  LocationOn, 
  Home, 
  TrendingUp, 
  Search,
  FilterList,
  ViewList,
  ViewModule
} from '@mui/icons-material';

const ResidentialProperties = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [viewMode, setViewMode] = useState('grid');
  const [filters, setFilters] = useState({
    priceRange: '',
    bedrooms: '',
    location: '',
    propertyType: ''
  });

  const propertyCategories = [
    {
      title: "Single Family Homes",
      description: "Traditional single-family residential properties with strong rental potential",
      count: 156,
      avgROI: "23%",
      priceRange: "$45K - $250K"
    },
    {
      title: "Condominiums",
      description: "Low-maintenance condo units perfect for first-time investors",
      count: 89,
      avgROI: "18%",
      priceRange: "$35K - $180K"
    },
    {
      title: "Townhouses",
      description: "Multi-level properties offering excellent space-to-value ratios",
      count: 67,
      avgROI: "25%",
      priceRange: "$65K - $320K"
    },
    {
      title: "Multi-Family",
      description: "Duplexes and small multi-family units for portfolio diversification",
      count: 43,
      avgROI: "28%",
      priceRange: "$85K - $450K"
    }
  ];

  const sampleProperties = [
    {
      id: 1,
      type: "Single Family",
      address: "789 Maple Street, Trenton, NJ",
      bedrooms: 3,
      bathrooms: 2,
      sqft: 1800,
      upsetValue: "$85,000",
      estimatedValue: "$125,000",
      roi: "47%",
      auctionDate: "Dec 22, 2024",
      condition: "Good",
      yearBuilt: 2016
    },
    {
      id: 2,
      type: "Condominium",
      address: "234 River View Dr, Camden, NJ",
      bedrooms: 2,
      bathrooms: 1,
      sqft: 1200,
      upsetValue: "$55,000",
      estimatedValue: "$78,000",
      roi: "42%",
      auctionDate: "Dec 25, 2024",
      condition: "Fair",
      yearBuilt: 2014
    },
    {
      id: 3,
      type: "Townhouse",
      address: "567 Oak Lane, Newark, NJ",
      bedrooms: 4,
      bathrooms: 3,
      sqft: 2400,
      upsetValue: "$125,000",
      estimatedValue: "$175,000",
      roi: "40%",
      auctionDate: "Dec 28, 2024",
      condition: "Excellent",
      yearBuilt: 2019
    }
  ];

  const investmentTips = [
    {
      title: "Location Analysis",
      description: "Research neighborhood trends, school districts, and local amenities that affect property values."
    },
    {
      title: "Condition Assessment",
      description: "Factor in renovation costs when calculating potential returns on investment."
    },
    {
      title: "Rental Market",
      description: "Analyze local rental rates and vacancy rates to project cash flow potential."
    },
    {
      title: "Exit Strategy",
      description: "Plan your exit strategy - will you rent, flip, or hold long-term?"
    }
  ];

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa', py: 4 }}>
      <Container maxWidth="lg">
        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h2" component="h1" sx={{ 
            fontWeight: 700, 
            color: '#1B5E20',
            mb: 2 
          }}>
            Residential Properties
          </Typography>
          <Typography variant="h6" sx={{ 
            color: '#666',
            maxWidth: '800px',
            mx: 'auto',
            lineHeight: 1.6
          }}>
            Explore residential foreclosure properties perfect for rental income, 
            fix-and-flip projects, or long-term investment strategies.
          </Typography>
        </Box>

        {/* Property Categories */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 3, color: '#1B5E20' }}>
            Property Categories
          </Typography>
          <Grid container spacing={3}>
            {propertyCategories.map((category, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card sx={{ 
                  height: '100%',
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'translateY(-2px)' }
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#1B5E20' }}>
                      {category.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                      {category.description}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <Typography variant="caption" sx={{ color: '#666' }}>Available</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {category.count}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="caption" sx={{ color: '#666' }}>Avg ROI</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                          {category.avgROI}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="caption" sx={{ color: '#666' }}>Price Range</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {category.priceRange}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Search and Filters */}
        <Paper sx={{ p: 3, mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <FilterList sx={{ mr: 1, color: '#1B5E20' }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1B5E20' }}>
              Search & Filter Properties
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Search Location"
                placeholder="City, County, ZIP"
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: <Search sx={{ color: '#666', mr: 1 }} />
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Price Range</InputLabel>
                <Select
                  value={filters.priceRange}
                  label="Price Range"
                  onChange={(e) => handleFilterChange('priceRange', e.target.value)}
                >
                  <MenuItem value="0-50k">$0 - $50K</MenuItem>
                  <MenuItem value="50k-100k">$50K - $100K</MenuItem>
                  <MenuItem value="100k-200k">$100K - $200K</MenuItem>
                  <MenuItem value="200k+">$200K+</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Bedrooms</InputLabel>
                <Select
                  value={filters.bedrooms}
                  label="Bedrooms"
                  onChange={(e) => handleFilterChange('bedrooms', e.target.value)}
                >
                  <MenuItem value="1">1+</MenuItem>
                  <MenuItem value="2">2+</MenuItem>
                  <MenuItem value="3">3+</MenuItem>
                  <MenuItem value="4">4+</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Property Type</InputLabel>
                <Select
                  value={filters.propertyType}
                  label="Property Type"
                  onChange={(e) => handleFilterChange('propertyType', e.target.value)}
                >
                  <MenuItem value="single">Single Family</MenuItem>
                  <MenuItem value="condo">Condominium</MenuItem>
                  <MenuItem value="townhouse">Townhouse</MenuItem>
                  <MenuItem value="multi">Multi-Family</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button 
                  variant="contained" 
                  sx={{ bgcolor: '#1B5E20', flex: 1 }}
                  startIcon={<Search />}
                >
                  Search
                </Button>
                <Button 
                  variant="outlined"
                  onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
                >
                  {viewMode === 'grid' ? <ViewList /> : <ViewModule />}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Sample Properties */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 3, color: '#1B5E20' }}>
            Sample Properties
          </Typography>
          <Grid container spacing={3}>
            {sampleProperties.map((property) => (
              <Grid item xs={12} md={6} lg={4} key={property.id}>
                <Card sx={{ 
                  height: '100%',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.12)'
                  }
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Chip label={property.type} size="small" color="primary" />
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        Built {property.yearBuilt}
                      </Typography>
                    </Box>

                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#1B5E20' }}>
                      {property.address}
                    </Typography>

                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={4}>
                        <Typography variant="caption" sx={{ color: '#666' }}>Beds</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {property.bedrooms}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="caption" sx={{ color: '#666' }}>Baths</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {property.bathrooms}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="caption" sx={{ color: '#666' }}>Sq Ft</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {property.sqft.toLocaleString()}
                        </Typography>
                      </Grid>
                    </Grid>

                    <Box sx={{ bgcolor: '#f5f5f5', borderRadius: 2, p: 2, mb: 2 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="caption" sx={{ color: '#666' }}>Upset Value</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1B5E20' }}>
                            {property.upsetValue}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" sx={{ color: '#666' }}>Est. Value</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {property.estimatedValue}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#666' }}>Potential ROI</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: '#2e7d32' }}>
                          {property.roi}
                        </Typography>
                      </Box>
                      <Chip 
                        label={property.condition} 
                        size="small" 
                        color={property.condition === 'Excellent' ? 'success' : property.condition === 'Good' ? 'warning' : 'default'}
                      />
                    </Box>

                    <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                      Auction: {property.auctionDate}
                    </Typography>

                    <Button 
                      variant="contained" 
                      fullWidth
                      sx={{ bgcolor: '#1B5E20' }}
                    >
                      View Details
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Investment Tips */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 3, color: '#1B5E20' }}>
            Residential Investment Tips
          </Typography>
          <Grid container spacing={3}>
            {investmentTips.map((tip, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card sx={{ height: '100%', bgcolor: '#e8f5e8' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1B5E20' }}>
                      {tip.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      {tip.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Call to Action */}
        <Box sx={{ 
          textAlign: 'center', 
          bgcolor: '#1B5E20', 
          color: 'white',
          borderRadius: 3,
          p: 4
        }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
            Start Building Your Portfolio
          </Typography>
          <Typography variant="h6" sx={{ mb: 3, opacity: 0.9 }}>
            Access thousands of residential foreclosure properties across New Jersey
          </Typography>
          <Button 
            variant="contained" 
            size="large"
            sx={{ 
              bgcolor: 'white',
              color: '#1B5E20',
              fontWeight: 600,
              px: 4,
              mr: 2
            }}
          >
            Browse All Properties
          </Button>
          <Button 
            variant="outlined" 
            size="large"
            sx={{ 
              color: 'white',
              borderColor: 'white',
              fontWeight: 600,
              px: 4
            }}
          >
            Get Investment Guide
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default ResidentialProperties;














